import React, { useEffect, useMemo, useRef, useState } from "react";
import Input from "@/app/components/ui/Inputs/Input";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useProductMoveGoodToBadAPI } from "@/app/hooks/api_hooks/Inventory/GoodInventory/useProductMoveGoodToBadAPI";
import { useReasonListAPI } from "@/app/hooks/api_hooks/Inventory/GoodInventory/useReasonListAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { FiUpload } from "react-icons/fi";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import { IInventoryMoveGoodToBadPostBody } from "@/api_framework/api_modals/Inventory";
import { useNavigate } from "react-router";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";

export interface IGoodToBadModalProps {
  qty: number;
  reason: string;
  images: string;
}
const REMOVE_TITLES = ["Expired Product", "Damaged in Clinic - Damaged from Vendor", "Damaged in Clinic - Rat Bite", "Damaged in Clinic - During handling"];

const GoodToBadModalContent = ({
  formHook,
  selectedImage,
  setSelectedImage,
  reasonOptions,
  data,
  selectedReason,
  setSelectedReason,
  showImageError,
  setShowImageError,
}) => {
  const { forOnlyNumberWithoutZero } = useFormValidations();

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowImageError(false);
    const files = event.target.files;
    if (!files) return;
    const validTypes = ["image/png", "image/jpeg"];
    const maxSize = 5 * 1024 * 1024;
    Array.from(files).forEach((file) => {
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload PNG or JPEG images only.");
        return;
      }
      if (file.size > maxSize) {
        toast.error("File size must be less than 5MB.");
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        const fileExtension = file.type.split("/")[1];
        setSelectedImage((prevImages) => [...prevImages, { base64String, fileExtension }]);
      };
      reader.readAsDataURL(file);
    });
    event.target.value = "";
  };

  const handleRemoveImage = (index: number) => {
    setSelectedImage((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div className="items-center gap-16 inline-flex">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[150px]">
          <span className="font-normal text-black text-xs">Product</span>
        </div>
        <p>{data?.product_name || "Product not found"}</p>
      </div>
      <div className="gap-16 flex">
        <div className="p-2.5 w-[150px]">
          <span className="font-normal text-black text-xs">Lot Details</span>
        </div>
        <div className="flex mt-3 flex-col">
          <p>Lot: {data?.lot_number}</p>
          <p>Manufacturer: {data?.manufacturer_name}</p>
          <p>Exp Date: {data?.expiry_date}</p>
          <p>Mrp: {data?.mrp}</p>
        </div>
      </div>
      <div className="items-center gap-16 inline-flex">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[150px]">
          <span className="font-normal text-black text-xs">On hand:</span>
        </div>
        <p>{data?.qty}</p>
      </div>
      <div className="items-center gap-16 inline-flex">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[150px] h-[38px]">
          <span className="font-normal text-black text-xs">Qty</span>
          <span className="font-normal text-rose-600 text-xs">*</span>
        </div>
        <Input
          register={formHook.register("qty", {
            ...forOnlyNumberWithoutZero.validations,
            required: true,
            valueAsNumber: true,
            validate: (value) => {
              if (value === null || value === undefined || value === "") return "Qty is required";
              if (Number(value) === 0) return "Qty must be greater than 0";
              if (Number(value) > +data?.qty)
                return `Qty must be less than or equal to Good / Sellable Qty ${data?.qty}`;
              return true;
            },
            min: {
              value: 1,
              message: "Qty must be a positive number or greater than 0",
            },
            max: {
              value: +data?.qty,
              message: "Qty must be less than or equal to Good / Sellable Qty",
            },
            // validate: (value) => {
            //   if (!/^\d+$/.test(value)) {
            //     return "Qty must be a whole number";
            //   }
            //   return Number(value) > 0 || "Qty must be a positive number";
            // },
          })}
          type="number"
          min={1}
          max={+data?.qty}
          // step="1"
          onKeyDown={(e) => {
            const invalidChars = ["+", "-", ".", ","];
            if (invalidChars.includes(e.key)) {
              e.preventDefault();
            }
          }}
          onWheel={(e) => e.currentTarget.blur()}
          value={formHook.watch("qty") ?? ""}
          disabled={data.bad_qty > 0}
          errorMessages={[
            forOnlyNumberWithoutZero.errors,
            { type: "required", message: "Qty is required" },
            { type: "min", message: "Qty must be a positive number or greater than 0" },
            { type: "max", message: `Qty must be less than or equal to Good / Sellable Qty ${data?.qty}` },
          ]}
          className="h-9 w-[290px] text-xs rounded-md border-slate-400"
          fieldError={formHook.formState.errors.qty}
          placeholder="Enter Qty"
        />
      </div>
      <div className="justify-start items-center gap-16 inline-flex mt-5">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[150px] h-[38px]">
          <span className="font-normal text-black text-xs">Provide Reason</span>
          <span className="font-normal text-rose-600 text-xs">*</span>
        </div>
        <div className="w-72">
          <SearchableSelectMenu
            errorMessages={[{ type: "required", message: "Reason is required" }]}
            onSelectItem={(item) => {
              if (item) {
                formHook.setValue("reason", item.title);
                formHook.clearErrors("reason");
                setSelectedReason(item.title);
              }
            }}
            fieldError={formHook.formState.errors.reason}
            register={formHook.register("reason", { required: true })}
            selectItems={reasonOptions}
            placeholder="Select Reason"
            showTooltips={false}
            showTypedErrors={true}
            showDropdownIcon={true}
            defaultSelected={reasonOptions?.filter((oc) => oc.title === formHook.watch("reason"))[0]}
            disabled={data.bad_qty > 0}
            listBoxClassName="w-full"
            className="text-gray-800 "
            containerClassName="w-full"
          />
        </div>
      </div>
      <div className="justify-start items-center gap-16 flex mt-5 mb-5">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[85px] h-[38px]">
          <span className="font-normal text-black text-xs">Images</span>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {selectedImage.map((image, index) => (
            <div key={index} className="relative mt-3 mr-3">
              <img src={image.base64String} alt="Selected" className="w-20 h-14 object-contain" />
              <button
                onClick={() => handleRemoveImage(index)}
                className="absolute top-0 right-0 bg-white rounded-full p-1 shadow-md"
              >
                &times;
              </button>
            </div>
          ))}
          {selectedImage.length > 0 ? (
            <label className="flex items-center text-center gap-2 text-sm font-medium text-[rgb(54,130,238)] cursor-pointer">
              <FiUpload /> Upload images
              <input
                type="file"
                accept="image/png, image/jpeg"
                multiple
                className="hidden"
                onChange={handleFileUpload}
              />
            </label>
          ) : null}
        </div>
        <div>
          {selectedImage.length === 0 && (
            <div
              className={`border ${selectedReason !== "Audit Mismatch" && selectedImage.length === 0 && showImageError ? "border-red-600" : "border-gray-200"} p-5 rounded-md w-[290px] flex flex-col items-center`}
            >
              <label className="flex items-center text-center gap-2 font-medium text-[rgb(54,130,238)] cursor-pointer">
                <FiUpload />
                Upload
                <input
                  type="file"
                  accept="image/png, image/jpeg"
                  multiple
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </label>
              <p className="mt-2 text-gray-600 text-center">Each image must not exceed 5MB</p>
            </div>
          )}
          {selectedReason !== "Audit Mismatch" && selectedImage.length === 0 && (
            <p className={`mt-2 text-xs ${showImageError ? "text-red-600" : "text-black"}`}>
              Image is required for Expired & Damaged.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const GoodToBadModal = () => {
  const { setInvisible, setModalData } = useModalActions("GOOD_TO_BAD_MODAL");
  const { execute: productMoveToBad, status } = useProductMoveGoodToBadAPI();
  const formHook = useForm<IGoodToBadModalProps>({
    mode: "onChange",
    defaultValues: {},
  });
  const [selectedImage, setSelectedImage] = useState<{ base64String: string; fileExtension: string }[]>([]);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [showImageError, setShowImageError] = useState(false);
  const { reasonList } = useRouteInfo("INVENTORY")?.routeState?.state as IInventoryState;
  const reasonOptions = useSelectMenuReducer(reasonList, "reason", "id");
  const modalState = useModalState("GOOD_TO_BAD_MODAL");
  const { vetic_variant_id, unique_row_id, sub_category_id, bad_inventory_id } = modalState || {};
  const [isloading, setIsLoading] = useState(false);
  const { isOpen: isModalOpen } = useModalState("GOOD_TO_BAD_MODAL");
  const qtyValue = formHook.watch("qty");
  const reasonValue = formHook.watch("reason");
  const isValidQty = /^\d+$/.test(qtyValue?.toString() || "");
  const isButtonDisabled = !isValidQty || !Number(qtyValue) || !reasonValue;

  const [goodToBadReason, setGoodToBadReason] = useState([]);

  const userPermissions = useAppSelector((state) => state?.auth?.userPermissions);

  // RBAC: GOOD_TO_BAD_CLINIC_REASONS_FILTER — legacy roles: clinic_ops, clinic_ops_doggyworld
  const isClinicOpps = useFeatureAccess("GOOD_TO_BAD_CLINIC_REASONS_FILTER");

  const hasInitialized = useRef(false);
  const roleName = userPermissions?.role_name;
  // RBAC: GOOD_TO_BAD_ALL_REASONS_ACTION — legacy role: central_ops
  const canSeeAllGoodToBadReasons = useFeatureAccess("GOOD_TO_BAD_ALL_REASONS_ACTION");

  useEffect(() => {
    if (
      !hasInitialized.current &&
      reasonOptions?.length > 0 && // ✅ ensure real data
      roleName // ✅ ensure permissions loaded
    ) {
      const filteredReasons = isClinicOpps
        ? reasonOptions.filter((reason) => REMOVE_TITLES.includes(reason.title))
        : reasonOptions;

      setGoodToBadReason(filteredReasons);

      hasInitialized.current = true;
    }
  }, [isClinicOpps, reasonOptions, roleName]);

  const reasonOptionsForDropdown = useMemo(() => {
    return canSeeAllGoodToBadReasons ? reasonOptions : goodToBadReason;
  }, [goodToBadReason, canSeeAllGoodToBadReasons, reasonOptions]);

  const resetModalState = () => {
    formHook.reset({ qty: NaN as number, reason: "" });
    setSelectedImage([]);
    setSelectedReason("");
    setShowImageError(false);
  };

  useEffect(() => {
    fetchReasonList();
  }, []);

  useEffect(() => {
    if (+modalState?.bad_qty > 0) {
      formHook.reset({
        qty: +modalState?.bad_qty,
        reason: modalState?.bad_reason,
      });
      setSelectedReason(modalState?.bad_reason);
    } else {
      formHook.reset({ qty: NaN as number, reason: "" });
      setSelectedReason("");
    }
  }, [modalState?.bad_qty, modalState?.bad_reason]);

  useEffect(() => {
    formHook.clearErrors();
    if (status) {
      formHook.setValue("qty", null);
      formHook.setValue("reason", null);
      setSelectedImage([]);
      resetModalState();
      setModalData({ status: true });
    } else {
      setIsLoading(false);
    }
  }, [status]);

  const fetchReasonList = useReasonListAPI();

  useEffect(() => {
    if (modalState.key) {
      setSelectedImage([]);
    }
  }, [modalState.key]);

  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  // ✅ read query params
  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const processId = queryParams.get("closing_process");
  const isClinicView = queryParams.get("is_clinic");
  const city_id = queryParams.get("city_id");

  const handleSubmit = async (data: IGoodToBadModalProps) => {
    if (isloading) return;
    const isValid = await formHook.trigger();
    let badValid = false;
    if (+modalState?.bad_qty > 0) {
      formHook.clearErrors("qty");
      formHook.clearErrors("reason");
      badValid = true;
    }
    const requiresImage = selectedReason === "Expired Product" || selectedReason === "Damaged in Clinic - Damaged from Vendor" || selectedReason === "Damaged in Clinic - Rat Bite" || selectedReason === "Damaged in Clinic - During handling";
    if (requiresImage && selectedImage.length === 0) {
      setShowImageError(true);
      return;
    }
    if (selectedImage.length > 0) {
      setShowImageError(false);
    }
    if (modalState.key && selectedImage.length === 0) {
      setShowImageError(true);
      toast.error("Image is required for Expired & Damaged.");
      return;
    }
    setShowImageError(false);
    setIsLoading(true);
    if (isValid || badValid) {
      const images = selectedImage.map((image) => ({
        base_64_string: image.base64String.split(",")[1],
        file_extension: image.fileExtension,
      }));
      // setIsFormValid(isValid);
      const moveBody: IInventoryMoveGoodToBadPostBody = {
        vetic_variant_id: vetic_variant_id,
        unique_row_id: unique_row_id,
        bad_quantity: +data?.qty,
        reason: data?.reason,
        sub_category_id: sub_category_id,
      };
      const payloadReupload: IInventoryMoveGoodToBadPostBody = {
        vetic_variant_id: vetic_variant_id,
        unique_row_id: unique_row_id,
        bad_quantity: modalState?.bad_qty,
        reason: modalState?.bad_reason,
        sub_category_id: sub_category_id,
      };
      const payload = modalState?.key
        ? { ...payloadReupload, re_upload_image: true, bad_inventory_id: bad_inventory_id, images }
        : { ...moveBody, images };
      if (images?.length > 0) {
        moveBody.images = images;
      }
      try {
        await productMoveToBad(payload, () => {
          resetModalState();
          setModalData({});
          setInvisible();
          processId &&
            navigate(
              `/app/closing/day-closing?closing_process=${processId}&city_id=${city_id}&clinic_ids=${queryClinics}&is_clinic=${isClinicView}&shift=${queryShift}&t=${Date.now()}`,
            );
        });
      } catch (error) {
        toast.error("Failed to move product");
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      setIsLoading(false);
    }
  }, [isModalOpen]);

  return (
    <ModalPrompt
      modalId="GOOD_TO_BAD_MODAL"
      showIcon={false}
      titleIcon=""
      dialogClassName="sm:my-8 sm:w-full sm:max-w-2xl sm:p-6 h-[600px]"
      button={{
        primaryButtonText: ` ${isloading ? "Updating..." : "Update"}`,
        secondaryButtonText: "Cancel",
        primaryButtonClassname: `px-6 py-[9px] rounded-md border border-slate-400 border-opacity-20 justify-center items-center gap-2 flex
    ${isButtonDisabled ? "cursor-not-allowed bg-gray-400" : "cursor-pointer bg-sky-900 hover"}`,
        primaryCallback: () => {
          // if (!isloading) {
          handleSubmit(formHook.getValues());
          // }
        },
        secondaryCallback: () => {
          formHook.clearErrors();
          formHook.setValue("qty", null);
          formHook.setValue("reason", null);
          setSelectedImage([]);
          resetModalState();
          setModalData({});
          setInvisible();
        },
        primaryButtonDisabled: isButtonDisabled || isloading,
      }}
      title="Move from good to bad"
      content={
        <GoodToBadModalContent
          formHook={formHook}
          selectedImage={selectedImage}
          setSelectedImage={setSelectedImage}
          reasonOptions={reasonOptionsForDropdown}
          data={modalState}
          selectedReason={selectedReason}
          setSelectedReason={setSelectedReason}
          showImageError={showImageError}
          setShowImageError={setShowImageError}
        />
      }
    />
  );
};

export default GoodToBadModal;
